import moment from 'moment';
import { ACCOUNT_TYPES, colorFromName, iconForAccount } from './accountIcons.js';

/** Mirrors PlaidBankAccount.LIABILITY_TYPES; repeated so this file stays free of the model layer. */
const LIABILITY_TYPES = ['credit', 'loan'];

/**
 * Everything that tells one account from another, worked out once from whichever shape the account
 * arrived in, so AccountLabel and the pickers' search read the same thing.
 *
 * Three shapes reach here:
 *  - a PlaidBankAccount model, whose balances are parsed (`current` in cents, `created_at` a moment);
 *  - Plaid_Account_Serializer's raw output, whose balances are as the server sent them (`current` in
 *    dollars, `created_at` a unix time);
 *  - import/rules' available_accounts, the same serializer's output with `latest_balance` (cents) and
 *    `latest_balance_at` beside it instead of a balances list.
 *
 * `institution` is the group the account sits in, when the caller has it: a PlaidAccount model or the
 * raw linked_plaid_accounts row. `hint` is the account's entry in twinHints() of the list it is shown in. Without one, the account's own `institution` (the group as drawn by
 * the serializer, shaped like a plaid_institutions row) stands in.
 *
 * @returns {{
 *   id: number, name: string, originalName: ?string, institutionName: ?string,
 *   badge: { institution: ?object, name: string, color: ?string },
 *   mask: ?string, hint: ?string, typeLabel: ?string, icon: string, color: string, isLiability: boolean,
 *   balance: ?number, balanceDate: ?string, lastActivity: ?string, closed: boolean,
 * }}
 */
export function accountIdentity(account, institution = null, hint = null) {
	const bankName = account.official_name || account.name || null;
	const name = shownName(account);
	const group = describeInstitution(account, institution);
	const isLiability = LIABILITY_TYPES.includes(account.type);
	const latest = latestBalance(account);

	return {
		id: account.id,
		name,
		/** Only when a name the budget chose hides what the bank calls it. */
		originalName: account.display_name && bankName && bankName !== account.display_name ? bankName : null,
		institutionName: group.name || null,
		badge: { institution: group.logo, name: group.name || '', color: group.color },
		mask: account.mask ? `••${account.mask}` : null,
		/** What tells it from a same-named account when neither has digits (twinHints()); shown where the mask would be. */
		hint: account.mask ? null : (hint || null),
		typeLabel: typeLabel(account.type),
		icon: iconForAccount(account),
		color: account.color || group.color || colorFromName(group.name || name),
		isLiability,
		/** Signed as the Banks page signs it: money owed reads negative. */
		balance: latest ? (isLiability ? -latest.cents : latest.cents) : null,
		balanceDate: latest ? latest.date : null,
		lastActivity: day(account.last_transaction_at),
		closed: account.is_active === false || account.is_active === 0,
	};
}

/** The words a person reads for a stored type; an unknown type as it is stored. */
export function typeLabel(type) {
	if(!type) {
		return null;
	}
	const known = ACCOUNT_TYPES.find((option) => option.value === type);
	return known ? known.label : type;
}

/**
 * The text a picker filters on: everything worth typing, so a display name, the bank's name, the
 * institution or the last digits all find the same account.
 */
export function accountSearchText(identity) {
	return [
		identity.name,
		identity.originalName,
		identity.institutionName,
		identity.mask,
		identity.hint,
		identity.typeLabel,
	].filter(Boolean).join(' ');
}

/** The name an account is shown under: the one the budget gave it, else the bank's. */
export function shownName(account) {
	return account.display_name || account.official_name || account.name || `Account ${account.id}`;
}

/**
 * A few words for each account that has no last digits and shares its shown name with another open
 * account, to stand where the digits would: when it was linked, and an account it was linked beside.
 * Two Vanguard "RothIra"s with no mask read "linked Jan 8, 2021 · with Individual" and "linked Jan 20,
 * 2021". The day is given only when the month would not tell them apart.
 *
 * "With" names an open account in the same group (the Plaid login it came from) under another name,
 * the first in the list's order when there are several. An account with digits, or a name of its own,
 * gets nothing: it is told apart already.
 *
 * `accounts` is every account the list shows, flat, in any shape accountIdentity() reads, carrying
 * the serializer's `linked_at` (Y-m-d) and `linked_plaid_account_id`.
 *
 * @returns {Map<number, string>} By account id.
 */
export function twinHints(accounts) {
	const list = (accounts || []).filter(Boolean);
	const nameKey = (account) => shownName(account).trim().toLowerCase();
	const openByName = new Map();
	list.filter(isOpen).forEach((account) => {
		const key = nameKey(account);
		openByName.set(key, [...(openByName.get(key) || []), account]);
	});

	const hints = new Map();
	list.forEach((account) => {
		if(account.mask) {
			return;
		}
		const key = nameKey(account);
		const twins = (openByName.get(key) || []).filter((other) => other.id !== account.id);
		if(!twins.length) {
			return;
		}

		const parts = [];
		const linked = day(account.linked_at);
		if(linked) {
			const month = moment(linked, 'YYYY-MM-DD').format('MMM YYYY');
			const sameMonth = twins.some((twin) => {
				const theirs = day(twin.linked_at);
				return theirs && moment(theirs, 'YYYY-MM-DD').format('MMM YYYY') === month;
			});
			parts.push('linked ' + (sameMonth ? moment(linked, 'YYYY-MM-DD').format('MMM D, YYYY') : month));
		}
		const group = account.linked_plaid_account_id;
		if(group !== null && group !== undefined) {
			const beside = list.find((other) => other.id !== account.id
				&& isOpen(other)
				&& Number(other.linked_plaid_account_id) === Number(group)
				&& nameKey(other) !== key);
			if(beside) {
				parts.push('with ' + shownName(beside));
			}
		}
		if(parts.length) {
			hints.set(account.id, parts.join(' · '));
		}
	});
	return hints;
}

function isOpen(account) {
	return !(account.is_active === false || account.is_active === 0);
}

/**
 * The name, colour and logo an account is shown under: its institution's (a PlaidAccount model or
 * raw group) when given, else what the account itself carries.
 */
export function describeInstitution(account, institution) {
	if(institution) {
		const name = institution.getName
			? institution.getName()
			: (institution.institution_name || (institution.institution && institution.institution.name) || '');
		const color = institution.getColor
			? institution.getColor()
			: (institution.color || (institution.institution && institution.institution.primary_color) || null);
		return { name, color, logo: institution.institution || null };
	}
	const own = (account && account.institution) || null;
	return {
		name: (own && own.name) || '',
		color: (own && own.primary_color) || null,
		logo: own,
	};
}

/** The newest balance as { cents, date }, in the stored sign, or null when there is none. */
function latestBalance(account) {
	if(account.latest_balance !== undefined && account.latest_balance !== null) {
		return { cents: Number(account.latest_balance), date: day(account.latest_balance_at) };
	}
	let newest = null;
	(account.balances || []).forEach((balance) => {
		const parsed = moment.isMoment(balance.created_at);
		const when = parsed ? balance.created_at : moment.unix(balance.created_at);
		if(balance.current === null || balance.current === undefined || !when.isValid()) {
			return;
		}
		if(!newest || when.isAfter(newest.when)) {
			newest = {
				when,
				cents: parsed ? balance.current : Math.round(balance.current * 100),
			};
		}
	});
	return newest ? { cents: newest.cents, date: newest.when.format('YYYY-MM-DD') } : null;
}

/** YYYY-MM-DD from a date or datetime string, or null. */
function day(stamp) {
	if(!stamp) {
		return null;
	}
	const parsed = moment(String(stamp).slice(0, 10), 'YYYY-MM-DD', true);
	return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
}
